import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { TableToolbar } from './AdminUI';

const RANGE_OPTIONS = [
  { label: '7 days', value: '7d' },
  { label: '30 days', value: '30d' }
];

const formatNumber = (value) => new Intl.NumberFormat().format(value || 0);

const formatDateLabel = (value) => {
  const date = new Date(`${value}T00:00:00`);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const getReferrerLabel = (referrer) => {
  if (!referrer) return 'Direct';

  try {
    const url = new URL(referrer);
    return url.hostname.replace(/^www\./, '');
  } catch (error) {
    return referrer;
  }
};

const getPercentChange = (current, previous) => {
  if (!previous && !current) return 0;
  if (!previous) return 100;
  return Math.round(((current - previous) / previous) * 100);
};

const MetricCard = ({ label, value, icon, accent, detail, trend }) => (
  <div className="analytics-metric">
    <div className="analytics-metric-top">
      <span className="analytics-metric-label">{label}</span>
      <span className="analytics-metric-icon" style={{ color: accent }}>
        <i className={icon}></i>
      </span>
    </div>
    <div className="analytics-metric-value">{value}</div>
    <div className="analytics-metric-foot">
      {trend !== undefined ? (
        <span className={`analytics-trend ${trend >= 0 ? 'is-up' : 'is-down'}`}>
          <i className={`fa ${trend >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
          {Math.abs(trend)}%
        </span>
      ) : null}
      <span>{detail}</span>
    </div>
  </div>
);

const EmptyState = ({ icon, title, text }) => (
  <div className="analytics-empty">
    <i className={icon}></i>
    <strong>{title}</strong>
    <span>{text}</span>
  </div>
);

const analyticsTableStyles = {
  headCells: {
    style: {
      padding: '12px 16px',
      fontWeight: '600',
      fontSize: '13px',
      color: '#666666',
      backgroundColor: '#f8fafc',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }
  },
  cells: {
    style: {
      padding: '14px 16px',
      fontSize: '14px',
      color: '#222222'
    }
  },
  rows: {
    style: {
      minHeight: '52px'
    }
  }
};

const Analytics = () => {
  const { apiCall } = useAuth();
  const [summary, setSummary] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [recent, setRecent] = useState([]);
  const [range, setRange] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dailySearch, setDailySearch] = useState('');
  const [recentSearch, setRecentSearch] = useState('');
  const [dailyRowsPerPage, setDailyRowsPerPage] = useState(10);
  const [recentRowsPerPage, setRecentRowsPerPage] = useState(10);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const [sumRes, topRes, recRes] = await Promise.all([
          apiCall(`/analytics/summary?range=${range}`),
          apiCall(`/analytics/top-pages?range=${range}&limit=10`),
          apiCall('/analytics/recent?limit=50')
        ]);

        if (!sumRes.ok || !topRes.ok || !recRes.ok) {
          throw new Error('Analytics request failed');
        }

        const sumData = await sumRes.json();
        const topData = await topRes.json();
        const recData = await recRes.json();

        setSummary(sumData.data || []);
        setTopPages(topData.top || []);
        setRecent(Array.isArray(recData) ? recData : []);
      } catch (e) {
        setSummary([]);
        setTopPages([]);
        setRecent([]);
        setError('Analytics data could not be loaded right now.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [apiCall, range]);

  const insights = useMemo(() => {
    const totals = summary.reduce(
      (acc, day) => {
        acc.pageviews += day.pageviews || 0;
        acc.uniqueVisitors += day.uniqueVisitors || 0;
        return acc;
      },
      { pageviews: 0, uniqueVisitors: 0 }
    );

    const midpoint = Math.ceil(summary.length / 2);
    const previousPeriod = summary.slice(0, midpoint);
    const currentPeriod = summary.slice(midpoint);
    const previousViews = previousPeriod.reduce((acc, day) => acc + (day.pageviews || 0), 0);
    const currentViews = currentPeriod.reduce((acc, day) => acc + (day.pageviews || 0), 0);
    const visitorTrend = getPercentChange(
      currentPeriod.reduce((acc, day) => acc + (day.uniqueVisitors || 0), 0),
      previousPeriod.reduce((acc, day) => acc + (day.uniqueVisitors || 0), 0)
    );

    const topPage = topPages[0];
    const avgViews = summary.length ? Math.round(totals.pageviews / summary.length) : 0;
    const bestDay = summary.reduce((best, day) => {
      if (!best || (day.pageviews || 0) > (best.pageviews || 0)) return day;
      return best;
    }, null);

    return {
      totals,
      topPage,
      avgViews,
      bestDay,
      pageviewTrend: getPercentChange(currentViews, previousViews),
      visitorTrend
    };
  }, [summary, topPages]);

  const chart = useMemo(() => {
    const width = Math.max(720, summary.length * 72);
    const height = 260;
    const pad = { top: 24, right: 24, bottom: 38, left: 44 };
    const maxValue = Math.max(
      1,
      ...summary.map((day) => Math.max(day.pageviews || 0, day.uniqueVisitors || 0))
    );
    const xStep = summary.length > 1 ? (width - pad.left - pad.right) / (summary.length - 1) : 0;
    const plotHeight = height - pad.top - pad.bottom;
    const yFor = (value) => pad.top + plotHeight - (value / maxValue) * plotHeight;
    const xFor = (index) => pad.left + index * xStep;
    const lineFor = (key) => summary.map((day, index) => `${xFor(index)},${yFor(day[key] || 0)}`).join(' ');

    return {
      width,
      height,
      maxValue,
      pad,
      xFor,
      yFor,
      pageviewsLine: lineFor('pageviews'),
      visitorsLine: lineFor('uniqueVisitors')
    };
  }, [summary]);

  const recentColumns = useMemo(() => [
    {
      name: 'Path',
      selector: (row) => row.path,
      sortable: true,
      grow: 2,
      cell: (row) => <span className="analytics-path-cell">{row.path}</span>
    },
    {
      name: 'Source',
      selector: (row) => getReferrerLabel(row.referrer),
      sortable: true
    },
    {
      name: 'Time',
      selector: (row) => row.createdAt,
      sortable: true,
      right: true,
      cell: (row) => formatDateTime(row.createdAt)
    }
  ], []);

  const dailyColumns = useMemo(() => [
    {
      name: 'Date',
      selector: (row) => row.date,
      sortable: true,
      cell: (row) => formatDateLabel(row.date)
    },
    {
      name: 'Pageviews',
      selector: (row) => row.pageviews || 0,
      sortable: true,
      right: true,
      cell: (row) => formatNumber(row.pageviews)
    },
    {
      name: 'Visitors',
      selector: (row) => row.uniqueVisitors || 0,
      sortable: true,
      right: true,
      cell: (row) => formatNumber(row.uniqueVisitors)
    },
    {
      name: 'Views / Visitor',
      selector: (row) => (row.uniqueVisitors ? row.pageviews / row.uniqueVisitors : 0),
      sortable: true,
      right: true,
      cell: (row) => (row.uniqueVisitors ? (row.pageviews / row.uniqueVisitors).toFixed(1) : '0.0')
    }
  ], []);

  const filteredSummary = useMemo(() => {
    const query = dailySearch.trim().toLowerCase();
    if (!query) return summary;

    return summary.filter((day) => [
      formatDateLabel(day.date),
      day.pageviews,
      day.uniqueVisitors,
      day.uniqueVisitors ? (day.pageviews / day.uniqueVisitors).toFixed(1) : '0.0'
    ].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [summary, dailySearch]);

  const filteredRecent = useMemo(() => {
    const query = recentSearch.trim().toLowerCase();
    if (!query) return recent;

    return recent.filter((event) => [
      event.path,
      getReferrerLabel(event.referrer),
      formatDateTime(event.createdAt)
    ].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [recent, recentSearch]);

  return (
    <AdminLayout>
      <div className="analytics-page">
        <div className="analytics-header">
          <div>
            <h1 className="page-title">Analytics</h1>
            <p className="page-subtitle">Website traffic, visitor activity, and page performance.</p>
          </div>
          <div className="analytics-range" aria-label="Analytics date range">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={range === option.value ? 'active' : ''}
                onClick={() => setRange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {error ? (
          <div className="analytics-alert">
            <i className="fa fa-exclamation-circle"></i>
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="analytics-loading">
            <i className="fa fa-spinner fa-spin"></i>
            <span>Loading analytics...</span>
          </div>
        ) : (
          <>
            <div className="analytics-metrics-grid">
              <MetricCard
                label="Pageviews"
                value={formatNumber(insights.totals.pageviews)}
                icon="fa fa-eye"
                accent="#62B6E8"
                detail={`${formatNumber(insights.avgViews)} avg per day`}
                trend={insights.pageviewTrend}
              />
              <MetricCard
                label="Visitors"
                value={formatNumber(insights.totals.uniqueVisitors)}
                icon="fa fa-users"
                accent="#10b981"
                detail="Unique visitors"
                trend={insights.visitorTrend}
              />
              <MetricCard
                label="Top Page"
                value={insights.topPage?.path || '-'}
                icon="fa fa-star"
                accent="#f59e0b"
                detail={insights.topPage ? `${formatNumber(insights.topPage.count)} views` : 'No page data'}
              />
              <MetricCard
                label="Best Day"
                value={insights.bestDay ? formatDateLabel(insights.bestDay.date) : '-'}
                icon="fa fa-calendar-check-o"
                accent="#3b82f6"
                detail={insights.bestDay ? `${formatNumber(insights.bestDay.pageviews)} views` : 'No trend data'}
              />
            </div>

            <div className="analytics-layout">
              <section className="card analytics-chart-card">
                <div className="card-header">
                  <div>
                    <h2 className="card-title">Traffic Trend</h2>
                    <p className="analytics-card-subtitle">
                      {range === '7d' ? 'Last 7 days' : 'Last 30 days'} compared by day
                    </p>
                  </div>
                  <div className="analytics-legend">
                    <span><i className="legend-dot pageviews"></i>Pageviews</span>
                    <span><i className="legend-dot visitors"></i>Visitors</span>
                  </div>
                </div>
                <div className="analytics-chart-wrap">
                  {summary.every((day) => !day.pageviews && !day.uniqueVisitors) ? (
                    <EmptyState
                      icon="fa fa-line-chart"
                      title="No traffic yet"
                      text="Public page visits will appear here once visitors start browsing."
                    />
                  ) : (
                    <svg
                      className="analytics-line-chart"
                      viewBox={`0 0 ${chart.width} ${chart.height}`}
                      role="img"
                      aria-label="Pageviews and visitors by day"
                    >
                      {[0, 0.25, 0.5, 0.75, 1].map((mark) => {
                        const y = chart.yFor(chart.maxValue * mark);
                        return (
                          <g key={mark}>
                            <line x1={chart.pad.left} y1={y} x2={chart.width - chart.pad.right} y2={y} />
                            <text x={chart.pad.left - 12} y={y + 4} textAnchor="end">
                              {Math.round(chart.maxValue * mark)}
                            </text>
                          </g>
                        );
                      })}
                      <polyline className="pageviews-line" points={chart.pageviewsLine} />
                      <polyline className="visitors-line" points={chart.visitorsLine} />
                      {summary.map((day, index) => (
                        <g key={day.date}>
                          <circle className="pageviews-point" cx={chart.xFor(index)} cy={chart.yFor(day.pageviews || 0)} r="4" />
                          <circle className="visitors-point" cx={chart.xFor(index)} cy={chart.yFor(day.uniqueVisitors || 0)} r="4" />
                          <text x={chart.xFor(index)} y={chart.height - 12} textAnchor="middle">
                            {formatDateLabel(day.date)}
                          </text>
                        </g>
                      ))}
                    </svg>
                  )}
                </div>
              </section>

              <section className="card analytics-side-card">
                <div className="card-header">
                  <h2 className="card-title">Top Pages</h2>
                </div>
                <div className="analytics-top-pages">
                  {topPages.length === 0 ? (
                    <EmptyState
                      icon="fa fa-file-text-o"
                      title="No ranked pages"
                      text="The ranking will fill in after public pages receive views."
                    />
                  ) : (
                    topPages.map((page, index) => {
                      const max = Math.max(1, topPages[0]?.count || 1);
                      const percent = Math.round(((page.count || 0) / max) * 100);

                      return (
                        <div className="analytics-page-row" key={`${page.path}-${index}`}>
                          <div className="analytics-rank">{index + 1}</div>
                          <div className="analytics-page-main">
                            <div className="analytics-page-title">{page.path}</div>
                            <div className="analytics-bar">
                              <span style={{ width: `${percent}%` }}></span>
                            </div>
                          </div>
                          <div className="analytics-page-count">{formatNumber(page.count)}</div>
                        </div>
                      );
                    })
                  )}
                </div>
              </section>
            </div>

            <div className="analytics-detail-grid">
              <section className="card">
                <div className="card-header">
                  <h2 className="card-title">Daily Breakdown</h2>
                </div>
                <TableToolbar
                  search={dailySearch}
                  onSearchChange={setDailySearch}
                  rowsPerPage={dailyRowsPerPage}
                  onRowsPerPageChange={setDailyRowsPerPage}
                  placeholder="Search daily data..."
                />
                <DataTable
                  columns={dailyColumns}
                  data={filteredSummary}
                  customStyles={analyticsTableStyles}
                  pagination
                  paginationPerPage={dailyRowsPerPage}
                  paginationRowsPerPageOptions={[10, 20, 30]}
                  paginationComponentOptions={{ noRowsPerPage: true }}
                  noDataComponent={
                    <EmptyState
                      icon="fa fa-calendar-o"
                      title="No daily analytics available"
                      text="Daily traffic summaries will appear here."
                    />
                  }
                  dense
                  striped
                />
              </section>

              <section className="card">
                <div className="card-header">
                  <div>
                    <h2 className="card-title">Recent Activity</h2>
                    <p className="analytics-card-subtitle">Latest pageview events</p>
                  </div>
                </div>
                <TableToolbar
                  search={recentSearch}
                  onSearchChange={setRecentSearch}
                  rowsPerPage={recentRowsPerPage}
                  onRowsPerPageChange={setRecentRowsPerPage}
                  placeholder="Search activity..."
                />
                <DataTable
                  columns={recentColumns}
                  data={filteredRecent}
                  customStyles={analyticsTableStyles}
                  pagination
                  paginationPerPage={recentRowsPerPage}
                  paginationRowsPerPageOptions={[10, 20, 30]}
                  paginationComponentOptions={{ noRowsPerPage: true }}
                  noDataComponent={
                    <EmptyState
                      icon="fa fa-clock-o"
                      title="No recent activity yet"
                      text="Latest pageview events will appear here."
                    />
                  }
                  dense
                  striped
                />
              </section>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default Analytics;
